import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { X } from 'lucide-react';
import DeliveryCommentsSection from './DeliveryCommentsSection';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useEffect, useState } from 'react';
import { displayError } from '@/lib/error';
import { EMOJI_REGEX } from '@/utils/constant';
import { deliveryService } from '@/services/deliveryService';
import { DeliveryComment } from '@/types/delivery.types';

import { GenericErrorResponse } from '@brainvaulttech/dgr-react-sdk';



interface DeliveryCommentsDialogProps {
  workOrderId: string;
  open: boolean;
  onClose: () => void;
  onCommentAdded?: () => void;
}

const DeliveryCommentsDialog = ({ workOrderId, open, onClose, onCommentAdded }: DeliveryCommentsDialogProps) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [comment, setComment] = useState<string>('');
  const [commentsData, setCommentsData] = useState<DeliveryComment[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        setIsFetching(true);
        const response = await deliveryService.getWorkOrderComments(workOrderId);

        if (response.error) {
          displayError(response.error, 5000);
          return;
        }

        const data = response.data as { comments: DeliveryComment[] };
        const newComments = data?.comments ?? [];
        setCommentsData(newComments);
      } catch (e) {
        displayError(e as GenericErrorResponse, 5000);
      } finally {
        setIsFetching(false);
      }
    };

    void fetchComments();
  }, [workOrderId]);

  const saveHandler = async () => {
    setIsAddingComment(true);
    const response = await deliveryService.addWorkOrderComment(workOrderId, comment);

    if (response.error) {
      displayError(response.error, 5000);
      setIsAddingComment(false);
      return;
    }

    setComment('');
    if (response.data) {
      setCommentsData((prev) => [...prev, response.data as DeliveryComment]);
    }
    setRefreshKey((prev) => prev + 1);
    setIsAddingComment(false);

    if (onCommentAdded) {
      onCommentAdded();
    }
  };

  return (
    <Sheet modal={false} open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white flex flex-col gap-0"
        side="right"
        tabIndex={-1}
      >
        <SheetHeader className="flex flex-col gap-4 p-6 pb-0 sticky top-0 bg-white">
          <SheetTitle className={`flex flex-col gap-4 bg-white  pb-0`}>
            <div className="flex flex-row justify-between">
              <span className="font-semibold text-xl">Comments</span>
              <div className="flex flex-row gap-2 h-[32px] [&_div]:border-[1px] [&_div]:p-[6px] [&_div]:rounded-md [&_div]:justify-center">
                <div onClick={onClose}>
                  <X color="#676767" className="h-[20px] w-[20px] cursor-pointer" />
                </div>
              </div>
            </div>
          </SheetTitle>
          <SheetDescription className="sr-only">Comments section</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col h-full justify-between">
          <div className="py-2 h-full" key={refreshKey}>
            <DeliveryCommentsSection
              comments={commentsData}
              isFetching={isFetching}
            />
          </div>
          <div className="sticky bottom-0 p-2 bg-white">
            <div className="flex flex-row justify-between border-[1px] items-center border-bordercolor rounded-md gap-2 p-2 h-[64px]">
              <Textarea
                textAreaClassName="resize-none h-10 border-[0px] pt-[10px] px-2"
                className="border-none w-full p-0"
                onChange={(e) => {
                  const filteredValue = e.target.value.replace(EMOJI_REGEX, '');
                  e.target.value = filteredValue;
                  setComment(filteredValue.replace(/^\s+/, ''));
                }}
                value={comment}
                placeholder="Type your comment..."
              />
              <Button
                variant="outline"
                className="text-[#fff] h-[36px] w-[103px] py-2 px-4 rounded-md bg-primary border-primary hover:bg-[#12375C] hover:text-white"
                disabled={!comment || isAddingComment}
                isLoading={isAddingComment}
                onClick={saveHandler}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DeliveryCommentsDialog;
